import { Download, Mail, Monitor, Plus, Power, Printer, QrCode } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { DataTable, ColumnDef } from '../../../../components/shared/DataTable/DataTable';
import { Code128Barcode, code128Svg } from '../../../../components/shared/Code128Barcode/Code128Barcode';
import Modal from '../../../../components/shared/Modal/Modal';
import { MasterDetailLayout } from '../../../../components/shared/MasterDetail/MasterDetailLayout';
import { RowActionsMenu, editAction, deleteAction } from '../../../../components/shared/RowActionsMenu/RowActionsMenu';
import { SidebarForm } from '../../../../components/shared/SidebarForm/SidebarForm';
import { useToast } from '../../../../hooks/useToast';
import { deletePosTerminal, getPosTerminalToken, listPosTerminals, registerPosTerminal, updatePosTerminal } from '../../../../services/PosTerminalsService';
import { listStores } from '../../../../services/StoresService';
import { PosTerminal } from '../../../../types/PosTerminal.types';
import { Store } from '../../../../types/Store.types';
import '../shared-modules.css';

interface PosTerminalsPanelProps {
  /** Modo embebido: solo los TPV de esta tienda, en compacto (panel lateral de la tienda). */
  storeId?: string;
  /** Avisa de altas, bajas y cambios para refrescar el contador de la tienda. */
  onChanged?: () => void;
}

/** Terminales POS de las tiendas: cada TPV se crea dentro de una tienda y hereda su almacén. */
export const PosTerminalsPanel = ({ storeId: fixedStoreId, onChanged }: PosTerminalsPanelProps) => {
  const { notify } = useToast();
  const [terminals, setTerminals] = useState<PosTerminal[]>([]);
  const [editing, setEditing] = useState<PosTerminal | null>(null);
  const [status, setStatus] = useState<PosTerminal['status']>('active');
  const [reissueTarget, setReissueTarget] = useState<PosTerminal | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PosTerminal | null>(null);

  const reload = () => listPosTerminals().then(setTerminals).catch(err => console.error('Error fetching POS terminals:', err));
  const [stores, setStores] = useState<Store[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [code, setCode] = useState('');
  const [storeId, setStoreId] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [issuedToken, setIssuedToken] = useState<{ code: string; token: string } | null>(null);

  useEffect(() => {
    reload();
    let cancelled = false;
    listStores()
      .then(list => { if (!cancelled) setStores(list); })
      .catch(err => console.error('Error fetching stores:', err));
    return () => { cancelled = true; };
  }, []);

  const storeLabel = (t: PosTerminal) => {
    const found = stores.find(st => st.id === t.storeId);
    return found ? `${found.code} · ${found.name}` : t.storeName || (t.storeId ? `#${t.storeId}` : 'Sin tienda');
  };
  const warehouseOfStore = (id: string) => {
    const st = stores.find(x => x.id === id);
    return st ? (st.warehouseName || 'Sin almacén') : '';
  };

  const openForm = (terminal?: PosTerminal) => {
    setEditing(terminal || null);
    setStatus(terminal?.status || 'active');
    setCode(terminal?.code || '');
    setStoreId(terminal?.storeId || '');
    setFormError('');
    setFormOpen(true);
  };


  const handleSave = async () => {
    if (!code.trim()) {
      setFormError('El código de la terminal es obligatorio.');
      return;
    }
    if (!storeId) {
      setFormError('Elige la tienda en la que se crea el TPV.');
      return;
    }
    setSaving(true);
    setFormError('');
    try {
      if (editing && editing.id) {
        await updatePosTerminal(editing.id, storeId, status);
        notify('TPV', `Terminal ${editing.code} actualizada.`, 'info');
      } else {
        const { token } = await registerPosTerminal(code.trim(), storeId);
        setIssuedToken({ code: code.trim(), token });
        notify('TPV', `Terminal ${code.trim()} registrada correctamente.`, 'info');
      }
      await reload();
      if (onChanged) onChanged();
      setFormOpen(false);
    } catch (err) {
      setFormError((err as Error)?.message || 'Error al registrar la terminal.');
    } finally {
      setSaving(false);
    }
  };

  // Si el backend guarda el token se muestra tal cual; si no (terminales antiguas) se ofrece generar uno nuevo.
  const viewCode = async (t: PosTerminal) => {
    const stored = await getPosTerminalToken(t.id);
    if (stored) setIssuedToken({ code: t.code, token: stored });
    else setReissueTarget(t);
  };

  // El token solo existe en claro al generarlo, así que "ver código" genera uno nuevo.
  const confirmReissue = async () => {
    if (!reissueTarget) return;
    const target = reissueTarget;
    setReissueTarget(null);
    try {
      const { token } = await registerPosTerminal(target.code, target.storeId || undefined);
      setIssuedToken({ code: target.code, token });
      await reload();
    } catch (err) {
      notify('TPV', (err as Error)?.message || 'Error al generar el código.', 'warning');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    try {
      await deletePosTerminal(target.id);
      notify('TPV', `Terminal ${target.code} eliminada.`, 'info');
      await reload();
      if (onChanged) onChanged();
    } catch (err) {
      notify('TPV', (err as Error)?.message || 'Error al eliminar la terminal.', 'warning');
    }
  };

  const copyToken = () => {
    if (!issuedToken) return;
    navigator.clipboard?.writeText(issuedToken.token)
      .then(() => notify('TPV', 'Token copiado al portapapeles.', 'info'))
      .catch(() => notify('TPV', 'No se pudo copiar; selecciónelo manualmente.', 'info'));
  };

  const printBarcode = () => {
    if (!issuedToken) return;
    const w = window.open('', '_blank', 'width=480,height=360');
    if (!w) {
      notify('TPV', 'El navegador ha bloqueado la ventana de impresión.', 'info');
      return;
    }
    w.document.write(
      `<html><head><title>Terminal ${issuedToken.code}</title></head>` +
      `<body style="font-family:monospace;text-align:center;padding:24px">` +
      `<h3>Terminal TPV ${issuedToken.code}</h3>${code128Svg(issuedToken.token, 3, 110)}` +
      `<p style="font-size:20px;letter-spacing:4px">${issuedToken.token}</p></body></html>`
    );
    w.document.close();
    w.focus();
    w.print();
  };

  // Descarga PNG para adjuntarlo al correo (mailto no admite adjuntos).
  const downloadBarcode = () => {
    if (!issuedToken) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height + 40;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      ctx.fillStyle = '#000';
      ctx.font = '24px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(issuedToken.token, canvas.width / 2, img.height + 28);
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = `tpv-${issuedToken.code}.png`;
      a.click();
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(code128Svg(issuedToken.token, 3, 110))}`;
  };

  const emailBarcode = () => {
    if (!issuedToken) return;
    const subject = encodeURIComponent(`Terminal TPV ${issuedToken.code}`);
    const body = encodeURIComponent(
      `Terminal: ${issuedToken.code}\nToken de acceso: ${issuedToken.token}\n\nAdjunte la imagen del código de barras descargada.`
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const columns: ColumnDef<PosTerminal>[] = [
    { id: 'code', header: 'CÓDIGO', render: (t) => <span className="sales-channel-entity-id">{t.code}</span> },
    { id: 'store', header: 'TIENDA', render: (t) => <span>{storeLabel(t)}</span> },
    { id: 'warehouse', header: 'ALMACÉN', render: (t) => <span>{t.warehouseId ? (stores.find(st => st.id === t.storeId)?.warehouseName || `#${t.warehouseId}`) : 'Sin asignar'}</span> },
    { id: 'status', header: 'ESTADO', render: (t) => <span className={`module-badge ${t.status === 'active' ? 'active' : 'pending'}`}>{t.status === 'active' ? 'ACTIVA' : 'INACTIVA'}</span> },
    { id: 'createdAt', header: 'ALTA', render: (t) => <span>{t.createdAt ? new Date(t.createdAt).toLocaleString('es-ES') : ''}</span> }
  ];

  if (fixedStoreId) {
    const mine = terminals.filter(t => t.storeId === fixedStoreId);

    const addEmbedded = async () => {
      if (!code.trim()) { setFormError('Indica el código del TPV.'); return; }
      setSaving(true);
      setFormError('');
      try {
        const { token } = await registerPosTerminal(code.trim(), fixedStoreId);
        setIssuedToken({ code: code.trim(), token });
        notify('TPV', `Terminal ${code.trim()} registrada correctamente.`, 'info');
        setCode('');
        await reload();
        if (onChanged) onChanged();
      } catch (err) {
        setFormError((err as Error)?.message || 'Error al registrar la terminal.');
      } finally {
        setSaving(false);
      }
    };

    const toggleStatus = async (t: PosTerminal) => {
      try {
        await updatePosTerminal(t.id, t.storeId, t.status === 'active' ? 'inactive' : 'active');
        await reload();
      } catch (err) {
        notify('TPV', (err as Error)?.message || 'Error al modificar la terminal.', 'warning');
      }
    };

    return (
      <>
        {mine.length === 0 && <p style={{ fontSize: '0.75rem' }}>Esta tienda no tiene TPV.</p>}
        {mine.map(t => (
          <div key={t.id || t.code} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', padding: '0.375rem 0' }}>
            <div>
              <span className="sales-channel-entity-id">{t.code}</span>{' '}
              <span className={`module-badge ${t.status === 'active' ? 'active' : 'pending'}`}>{t.status === 'active' ? 'ACTIVA' : 'INACTIVA'}</span>
            </div>
            <RowActionsMenu
              actions={[
                { label: 'Ver código', icon: QrCode, tone: 'accent' as const, onSelect: () => viewCode(t) },
                { label: t.status === 'active' ? 'Desactivar' : 'Activar', icon: Power, onSelect: () => toggleStatus(t), disabled: !t.id },
                deleteAction(() => setDeleteTarget(t), { disabled: !t.id })
              ]}
            />
          </div>
        ))}
        {formError && <div className="module-form-error"><span>{formError}</span></div>}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }} data-keep-detail>
          <input type="text" value={code} onChange={(e) => setCode(e.target.value)} className="sidebar-form-input" placeholder="Código del nuevo TPV (TPV-001)" />
          <button type="button" className="module-action-btn" onClick={addEmbedded} disabled={saving} data-keep-detail>
            <Plus size={12} /> {saving ? 'Creando...' : 'Añadir TPV'}
          </button>
        </div>
        <p style={{ fontSize: '0.625rem', fontFamily: 'var(--font-mono)' }}>Requiere rol admin. El TPV usa el almacén y la serie de esta tienda.</p>
      <Modal
        isOpen={!!reissueTarget}
        type="warning"
        title="Ver código de barras"
        message={`Esta terminal no tiene el token guardado, así que se generará uno nuevo para ${reissueTarget?.code}. El código anterior dejará de funcionar. ¿Continuar?`}
        confirmText="Generar y ver"
        cancelText="Cancelar"
        onConfirm={confirmReissue}
        onCancel={() => setReissueTarget(null)}
      />

      <Modal
        isOpen={!!deleteTarget}
        type="urgent"
        title="Eliminar terminal"
        message={`¿Eliminar la terminal "${deleteTarget?.code}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <Modal
        isOpen={!!issuedToken}
        type="warning"
        title={`Token de la terminal ${issuedToken?.code || ''}`}
        confirmText="Copiar token"
        cancelText="Cerrar"
        onConfirm={copyToken}
        onCancel={() => setIssuedToken(null)}
      >
        <p style={{ fontSize: '0.75rem' }}>Puede volver a verlo cuando quiera desde Ver código en la tabla.</p>
        {issuedToken && <Code128Barcode value={issuedToken.token} />}
        <div style={{ fontFamily: 'var(--font-mono)', userSelect: 'all', letterSpacing: '0.25rem' }}>{issuedToken?.token}</div>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button type="button" className="module-action-btn" onClick={printBarcode}><Printer size={12} /> Imprimir</button>
          <button type="button" className="module-action-btn" onClick={downloadBarcode}><Download size={12} /> Descargar PNG</button>
          <button type="button" className="module-action-btn" onClick={emailBarcode}><Mail size={12} /> Enviar por correo</button>
        </div>
      </Modal>
      </>
    );
  }

  return (
    <>
      <div className="module-toolbar">
        <button type="button" className="module-action-btn" onClick={() => openForm()} data-keep-detail>
          <Plus size={12} /> Nueva terminal TPV
        </button>
      </div>

      <MasterDetailLayout isOpen={formOpen}>
      <DataTable
        data={terminals}
        columns={columns}
        title={<><Monitor size={14} className="module-panel-title-icon" /> Terminales_TPV</>}
        subtitle={`TOTAL: ${terminals.length}`}
        getRowId={(t) => t.id || t.code}
        emptyText="Ninguna terminal registrada"
        actionsHeader="ACCIONES"
        actionsColumn={(t) => (
          <RowActionsMenu
            actions={[
              { label: 'Ver código', icon: QrCode, tone: 'accent', onSelect: () => viewCode(t) },
              { ...editAction(() => openForm(t)), disabled: !t.id, title: t.id ? undefined : 'Requiere pos_terminals_list' },
              deleteAction(() => setDeleteTarget(t), { disabled: !t.id, title: t.id ? undefined : 'Requiere pos_terminals_list' })
            ]}
          />
        )}
      />

        {formOpen && (
        <SidebarForm
          title={editing ? 'Editar_Terminal_TPV' : 'Alta_Terminal_TPV'}
          onClose={() => setFormOpen(false)}
          submitLabel={saving ? 'Guardando...' : editing ? 'Guardar' : 'Generar terminal'}
          submitDisabled={saving}
          onSubmit={handleSave}
        >
          {formError && <div className="module-form-error"><span>{formError}</span></div>}
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Código *</label>
            <input type="text" value={code} onChange={(e) => setCode(e.target.value)} disabled={Boolean(editing)} className="sidebar-form-input" placeholder="TPV-001" />
          </div>
          <div className="sidebar-form-group">
            <label className="sidebar-form-label">Tienda *</label>
            <select value={storeId} onChange={(e) => setStoreId(e.target.value)} className="sidebar-form-input">
              <option value="">Selecciona una tienda...</option>
              {stores.map(st => <option key={st.id} value={st.id}>{st.code} · {st.name}</option>)}
            </select>
            {storeId && <p style={{ fontSize: '0.625rem', fontFamily: 'var(--font-mono)' }}>Almacén: {warehouseOfStore(storeId)}</p>}
          </div>
          {editing && (
            <div className="sidebar-form-group">
              <label className="sidebar-form-label">Estado</label>
              <select value={status} onChange={(e) => setStatus(e.target.value as PosTerminal['status'])} className="sidebar-form-input">
                <option value="active">ACTIVA</option>
                <option value="inactive">INACTIVA</option>
              </select>
            </div>
          )}
          <p style={{ fontSize: '0.625rem', fontFamily: 'var(--font-mono)' }}>
            {editing ? 'Editar no cambia el token de la terminal.' : 'Si el código ya existe se regenera su token y se actualiza la tienda. Requiere rol admin.'}
          </p>
        </SidebarForm>
        )}
      </MasterDetailLayout>

      <Modal
        isOpen={!!reissueTarget}
        type="warning"
        title="Ver código de barras"
        message={`Esta terminal no tiene el token guardado, así que se generará uno nuevo para ${reissueTarget?.code}. El código anterior dejará de funcionar. ¿Continuar?`}
        confirmText="Generar y ver"
        cancelText="Cancelar"
        onConfirm={confirmReissue}
        onCancel={() => setReissueTarget(null)}
      />

      <Modal
        isOpen={!!deleteTarget}
        type="urgent"
        title="Eliminar terminal"
        message={`¿Eliminar la terminal "${deleteTarget?.code}"? Esta acción no se puede deshacer.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <Modal
        isOpen={!!issuedToken}
        type="warning"
        title={`Token de la terminal ${issuedToken?.code || ''}`}
        confirmText="Copiar token"
        cancelText="Cerrar"
        onConfirm={copyToken}
        onCancel={() => setIssuedToken(null)}
      >
        <p style={{ fontSize: '0.75rem' }}>Puede volver a verlo cuando quiera desde Ver código en la tabla.</p>
        {issuedToken && <Code128Barcode value={issuedToken.token} />}
        <div style={{ fontFamily: 'var(--font-mono)', userSelect: 'all', letterSpacing: '0.25rem' }}>{issuedToken?.token}</div>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <button type="button" className="module-action-btn" onClick={printBarcode}><Printer size={12} /> Imprimir</button>
          <button type="button" className="module-action-btn" onClick={downloadBarcode}><Download size={12} /> Descargar PNG</button>
          <button type="button" className="module-action-btn" onClick={emailBarcode}><Mail size={12} /> Enviar por correo</button>
        </div>
      </Modal>
    </>
  );
};
